import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";

// Pinned-column state, shared by every grid in the app.
//
// Scopes:
//   "cri"     — the CRI ledger. ONE scope shared across all insurers (its export
//               format is identical everywhere), so insurerName is ignored.
//   "insurer" — per insurer, since each SOA has its own column vocabulary.
//   "paired"  — the Auto Matched grid, whose columns are composite (cri::x / ins::x /
//               meta::x) because one row shows both legs. It keeps its OWN scope: its
//               keys are a different vocabulary, and pinning there must not silently
//               reorder the statement tabs.
//
// Why a module-level cache rather than per-component fetch: the result page mounts
// several grids at once (two statement tabs, side-by-side, Other, notes, manual match,
// drill-downs), and each drill-down mounts more. A naive useEffect fetch would fire one
// GET per table per scope on every tab switch. Instead each scope is fetched AT MOST
// ONCE per page load and the resolved value is broadcast to every subscriber; writes
// update the cache optimistically and notify all subscribers, so pinning in one grid is
// reflected in the others immediately with no refetch.
//
// Pins are cosmetic. Every failure path here degrades to "no pins" and is swallowed —
// a preference must never surface an error over a reconciliation.

export type PinSide = "cri" | "insurer" | "paired";

const cache = new Map<string, string[]>();
const inflight = new Map<string, Promise<string[]>>();
const subscribers = new Map<string, Set<() => void>>();

function scopeKey(side: PinSide, insurerName: string): string {
  if (side === "cri") return "__cri__";
  if (side === "paired") return "paired:" + insurerName;
  return "ins:" + insurerName;
}

function notify(key: string): void {
  subscribers.get(key)?.forEach((fn) => fn());
}

function fetchScope(key: string, side: PinSide, insurerName: string): Promise<string[]> {
  const existing = inflight.get(key);
  if (existing) return existing;
  const p = api
    .columnPins(side === "cri" ? undefined : insurerName, side === "paired")
    .then((r) => {
      const cols = r.columns ?? [];
      cache.set(key, cols);
      notify(key);
      return cols;
    })
    .catch(() => {
      cache.set(key, []); // degrade to unpinned; never surface an error
      return [];
    })
    .finally(() => { inflight.delete(key); });
  inflight.set(key, p);
  return p;
}

/** Reset all cached pins — call on logout so the next user never inherits them. */
export function resetColumnPins(): void {
  cache.clear();
  inflight.clear();
  subscribers.forEach((_, k) => notify(k));
}

export interface PinState {
  pinned: string[];
  isPinned: (col: string) => boolean;
  toggle: (col: string) => void;
}

/**
 * Pinned columns for one scope. `insurerName` is ignored when side === "cri".
 * Returns pin order (which drives left-to-right column order) plus a toggle that
 * writes through to the server.
 */
export function useColumnPins(side: PinSide, insurerName: string): PinState {
  const key = scopeKey(side, insurerName);
  const [pinned, setPinned] = useState<string[]>(() => cache.get(key) ?? []);

  useEffect(() => {
    let alive = true;
    const sync = () => { if (alive) setPinned(cache.get(key) ?? []); };
    let subs = subscribers.get(key);
    if (!subs) { subs = new Set(); subscribers.set(key, subs); }
    subs.add(sync);
    sync();
    // Insurer/paired scopes need a name to resolve; the CRI scope never does.
    if (!cache.has(key) && (side === "cri" || insurerName)) {
      void fetchScope(key, side, insurerName);
    }
    return () => {
      alive = false;
      subs?.delete(sync);
      if (subs && subs.size === 0) subscribers.delete(key);
    };
  }, [key, side, insurerName]);

  const toggle = useCallback((col: string) => {
    const current = cache.get(key) ?? [];
    const next = current.includes(col)
      ? current.filter((c) => c !== col)
      : [...current, col];           // append: newest pin sits rightmost within the pinned block
    cache.set(key, next);            // optimistic — the grid reorders instantly
    notify(key);
    api
      .setColumnPins(side === "cri" ? undefined : insurerName, next, side === "paired")
      .then((r) => {
        // Trust the server's post-clamp echo so client and store can't drift.
        if (r.columns.join(" ") !== next.join(" ")) {
          cache.set(key, r.columns);
          notify(key);
        }
      })
      .catch(() => { /* keep the optimistic value; pins are cosmetic */ });
  }, [key, side, insurerName]);

  const isPinned = useCallback((col: string) => pinned.includes(col), [pinned]);
  return { pinned, isPinned, toggle };
}
